// src/services/claudeAPIService.js
// v9 - FINAL COMPLETE & VERIFIED FILE: Relative Path, Haiku Model, Correct Syntax & Structure. No extra text.

export default class claudeAPIService {
  // --- CONSTRUCTOR ---
  constructor() {
    // Use relative path for both development and production
    // This ensures proper routing through the static server
    this.apiEndpoint = '/api/game/claude-proxy';
    console.log('📡 Claude API Service initialized with endpoint:', this.apiEndpoint);
    console.log('📡 Current hostname:', window.location.hostname);
    console.log('📡 Is development:', window.location.hostname === 'localhost');

    // Use the requested Haiku model name.
    this.modelVersion = 'claude-3-haiku-20240307';

    this.retries = 3; // Total 4 attempts (1 initial + 3 retries)
    this.initialRetryDelay = 1500; // Start delay at 1.5 seconds
  }
  // --- END CONSTRUCTOR ---

  /**
   * Generate a game over title using Claude API via the server proxy.
   * @param {object} gameData - Context about the finished game.
   * @param {Array} playerScores - Array of player score objects for the ended game.
   * @param {string} currentUserId - The ID of the player viewing the results.
   * @returns {Promise<string>} The generated title or a fallback title.
   */
  async generateGameOverTitle(gameData, playerScores, currentUserId) {
    console.log("generateGameOverTitle called with:", {
      hasGameData: !!gameData,
      playerScoresLength: playerScores?.length || 0,
      currentUserId
    });

    if (!gameData || !playerScores || !Array.isArray(playerScores) || playerScores.length === 0) {
      console.warn("Invalid input for title generation, using fallback 'COSMIC CHAMPION'");
      return "COSMIC CHAMPION";
    }

    const currentPlayer = playerScores.find(p => p && p.userId === currentUserId) || { player: "Player", score: 0 };
    let playerRank = playerScores.findIndex(p => p && p.userId === currentUserId);
    playerRank = (playerRank === -1) ? playerScores.length : playerRank + 1;
    const winner = playerScores[0] || { player: "Unknown", score: 0 };

    let gameDuration = 180;
    if (gameData.endedAt && gameData.startedAt && typeof gameData.endedAt === 'number' && typeof gameData.startedAt === 'number') {
        gameDuration = Math.max(0, Math.floor((gameData.endedAt - gameData.startedAt) / 1000));
    } else if (gameData.duration && typeof gameData.duration === 'number') {
        gameDuration = Math.max(0, Math.floor(gameData.duration));
    }

    const prompt = `
      Create a short, punchy game over title (1-4 words max, ALL CAPS) for a space-themed professional services arcade game.
      Game details:
      - Players: ${playerScores.length}
      - Current Player: ${currentPlayer.player}, Score: ${currentPlayer.score}, Rank: #${playerRank}
      - Winner: ${winner.player}, Score: ${winner.score}
      - Mode: ${gameData.gameMode || "Survival"}, Duration: ${gameDuration}s
      Base the title on the player's performance (rank/percentile). Be dramatic/exciting.
      Examples: COSMIC OVERLORD (Win), TOP CONSULTANT (High Rank), STELLAR EFFORT (Mid), SPACE CADET (Low Rank).
      Respond ONLY with the title text in ALL CAPS. No extra words or explanation.`;

    console.log("Sending title request to Claude API via proxy");

    try {
      const response = await this.callClaudeApiWithRetry(prompt, 50);
      const title = this.extractResponse(response).trim().toUpperCase();
      if (title && /^[A-Z0-9 ]+$/.test(title) && title.length <= 30 && title.split(' ').length <= 4) {
        return title;
      }
      console.warn(`Invalid title format received: "${title}", using fallback.`);
      return "COSMIC VOYAGE";
    } catch (error) {
      console.error("Failed to generate title:", error);
      return "SPACE EXPLORER";
    }
  }

  /**
   * Generates technology words and classifies them into two categories via the server proxy.
   * @param {number} count - Number of words to generate.
   * @param {string} category1 - Name for the first category.
   * @param {string} category2 - Name for the second category.
   * @param {number} difficultyLevel - Difficulty level (1-3).
   * @returns {Promise<Array<{word: string, category: string}> | null>} - Array of word objects or null on failure.
   */
  async generateClassificationWords(count = 10, category1 = "Hardware", category2 = "Software", difficultyLevel = 1) {
    console.log(`generateClassificationWords called: Count=${count}, Categories=${category1}/${category2}, Difficulty=${difficultyLevel}`);

    let difficultyContext = "common and easily recognizable technology terms";
    if (difficultyLevel === 2) {
        difficultyContext = "moderately common technology terms, possibly including some basic acronyms or slightly more specific concepts";
    } else if (difficultyLevel >= 3) {
        difficultyContext = "more specific or nuanced technology terms, potentially including intermediate concepts, specific protocols, or less common acronyms";
    }

    const prompt = `
Generate exactly ${count} distinct technology terms suitable for a classification game.
The target difficulty for these terms is: ${difficultyContext}.
For each term, strictly classify it as either "${category1}" or "${category2}".

Prioritize terms appropriate for the requested difficulty. Avoid highly obscure or ambiguous terms *unless* high difficulty is requested.

Format the response ONLY as a valid JSON array of objects, like this example:
[
  { "word": "Monitor", "category": "${category1}" },
  { "word": "Algorithm", "category": "${category2}" },
  { "word": "Keyboard", "category": "${category1}" },
  { "word": "Database", "category": "${category2}" }
]

Do not include any introductory text, explanations, or markdown formatting before or after the JSON array.
The output must start with '[' and end with ']'.
Ensure each object has exactly "word" and "category" keys, with the category value being *exactly* "${category1}" or "${category2}".
`;

    console.log("Sending classification words request (with difficulty) to Claude API via proxy...");

    try {
      const response = await this.callClaudeApiWithRetry(prompt, 300 + count * 25);
      const rawResponseText = this.extractResponse(response).trim();
      console.log("Raw classification response from Claude:", rawResponseText);

      let parsedData = null;
      try {
        // Use a more flexible regex to capture the JSON array, even with surrounding text
        const jsonMatch = rawResponseText.match(/(\[[\s\S]*?\{[\s\S]*?\}\s*.*?\])/);
        if (jsonMatch && jsonMatch[0]) {
          parsedData = JSON.parse(jsonMatch[0]);
          console.log("Successfully parsed JSON data from response using regex.");
        } else {
          console.warn("Could not find JSON array via regex, attempting direct parse...");
          parsedData = JSON.parse(rawResponseText); // Try direct parse
        }
      } catch (jsonError) {
        console.error("Failed to parse JSON response:", jsonError, "Raw text:", rawResponseText);
        throw new Error("Invalid JSON format received from API");
      }

      if (!Array.isArray(parsedData) || parsedData.length === 0) {
        console.warn("Parsed data is not a valid array or is empty:", parsedData);
        throw new Error("Received invalid or empty data structure");
      }

      const validatedData = parsedData
        .map(item => {
          if (typeof item !== 'object' || !item.word || !item.category) return null;
          if (item.category !== category1 && item.category !== category2) return null;
          return { word: String(item.word).trim(), category: item.category };
        })
        .filter(item => item !== null && item.word.length > 0); // Ensure word isn't empty

      if (validatedData.length < count * 0.7) { // Require at least 70% valid items
         console.warn(`API returned insufficient valid items (${validatedData.length}/${count}).`);
         throw new Error("API returned insufficient valid items");
      }

      console.log(`Successfully generated and validated ${validatedData.length} classification words.`);
      return validatedData.slice(0, count); // Return exactly the requested count

    } catch (error) {
      console.error(`Failed to generate classification words (Difficulty ${difficultyLevel}):`, error);
      return null; // Indicate failure
    }
  }

  /**
   * Generate a personalized achievement using Claude API
   * @param {object} playerData - Data for the specific player
   * @param {number} rank - Player's rank
   * @param {number} totalPlayers - Total players in the game
   * @returns {Promise<object>} Object with name and description, or fallback
   */
  async generatePersonalizedAchievement(playerData, rank, totalPlayers) {
    console.log("generatePersonalizedAchievement called with:", { playerData, rank, totalPlayers });

    if (!playerData || typeof rank !== 'number' || typeof totalPlayers !== 'number' || totalPlayers <= 0) {
      console.warn("Invalid input for achievement generation, using fallback");
      return this.getFallbackAchievement(rank || totalPlayers + 1, totalPlayers || 1);
    }

    const percentile = Math.floor(((totalPlayers - rank) / totalPlayers) * 100);
    const isWinner = rank === 1;
    const isBottomTier = rank >= Math.floor(totalPlayers * 0.75) && totalPlayers > 1;

    let performanceContext = "solid";
    if (isWinner) performanceContext = "victorious";
    else if (rank <= Math.ceil(totalPlayers * 0.25)) performanceContext = "top-tier";
    else if (isBottomTier) performanceContext = "developing";

    const prompt = `
      Create a personalized achievement for a player in a professional services and consulting themed space arcade game.
      Player: ${playerData.player || "Player"}, Score: ${playerData.score || 0}, Rank: ${rank}/${totalPlayers} (${percentile}th percentile).
      Performance Level: ${performanceContext}.
      Desired Output:
      1. NAME: A creative 2-3 word achievement title in ALL CAPS.
      2. DESCRIPTION: A short, personalized 1-sentence description for the player.
      Tailor the tone: ${isWinner ? "Celebratory!" : isBottomTier ? "Encouraging/Humorous." : "Respectful/Acknowledging."}
      Reference game themes (asteroids, hyperspace, consulting, shortcuts) subtly if fitting.
      Format EXACTLY as:
      NAME: [THE NAME]
      DESCRIPTION: [The description.]
      No extra text.`;

    console.log("Sending achievement request to Claude API via proxy");

    try {
      const response = await this.callClaudeApiWithRetry(prompt, 150);
      const achievementText = this.extractResponse(response);

      const nameMatch = achievementText.match(/NAME:\s*(.*?)(?:\n|$)/);
      const descriptionMatch = achievementText.match(/DESCRIPTION:\s*(.*?)(?:\n|$)/);

      if (nameMatch && nameMatch[1] && descriptionMatch && descriptionMatch[1]) {
        return {
          name: nameMatch[1].trim().toUpperCase(),
          description: descriptionMatch[1].trim()
        };
      }

      console.warn("Could not parse achievement response, using fallback. Response:", achievementText);
      return this.getFallbackAchievement(rank, totalPlayers);
    } catch (error) {
      console.error("Failed to generate achievement:", error);
      return this.getFallbackAchievement(rank, totalPlayers);
    }
  }

  /**
   * Get a fallback achievement based on player's rank.
   */
  getFallbackAchievement(rank, totalPlayers) {
    const safeTotalPlayers = Math.max(1, totalPlayers);
    const safeRank = Math.max(1, Math.min(rank, safeTotalPlayers));

    if (safeRank === 1) { return { name: "COSMIC CHAMPION", description: "Your stellar performance earned you the top spot!" }; }
    else if (safeRank <= Math.ceil(safeTotalPlayers * 0.25)) { return { name: "STELLAR PERFORMER", description: "An impressive display of skill placed you among the best!" }; }
    else if (safeRank <= Math.ceil(safeTotalPlayers * 0.75)) { return { name: "SPACE NAVIGATOR", description: "You navigated the challenges with notable competence!" }; }
    else { return { name: "ASTEROID DODGER", description: "Every journey has its lessons. Keep aiming for the stars!" }; }
  }

  /**
   * Call Claude API (via internal proxy) with retry mechanism.
   * @param {string} prompt - The prompt to send.
   * @param {number} maxTokens - Max tokens for the response.
   * @returns {Promise<object>} The parsed JSON response from the proxy.
   * @throws {Error} If all retry attempts fail.
   */
  async callClaudeApiWithRetry(prompt, maxTokens = 100) {
    let attempts = 0;
    const maxAttempts = this.retries + 1;
    let lastError = null;

    while (attempts < maxAttempts) {
        try {
            console.log(`API request attempt ${attempts + 1}/${maxAttempts} for prompt: ${prompt.substring(0, 50)}...`);

            const controller = new AbortController();
            const timeoutDuration = 8000; // 8 second timeout for enterprise performance
            const timeoutId = setTimeout(() => {
                 console.warn(`API request attempt ${attempts + 1} timed out after ${timeoutDuration}ms.`);
                 controller.abort();
             }, timeoutDuration);

            console.log('🔍 Claude API Debug:', {
                endpoint: this.apiEndpoint,
                prompt: prompt.substring(0, 50) + '...',
                modelVersion: this.modelVersion,
                maxTokens,
                attempt: attempts + 1
            });

            const response = await fetch(this.apiEndpoint, { // Uses relative path from constructor
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    prompt,
                    modelVersion: this.modelVersion, // <<< USES THE CLASS PROPERTY (Haiku)
                    maxTokens
                }),
                signal: controller.signal
            });
            
            console.log('🔍 Claude API Response:', {
                status: response.status,
                ok: response.ok,
                headers: Object.fromEntries(response.headers.entries())
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                const errorText = await response.text().catch(() => `Status ${response.status}`);
                if (response.status === 504 || errorText.includes('timeout')) {
                    throw new Error(`API request timed out on server (Status ${response.status})`);
                }
                // Check for model not found error specifically
                if ((response.status === 404 || response.status === 400) && errorText.includes('model')) {
                    throw new Error(`Claude API Error: Model '${this.modelVersion}' not found or unavailable. Check model name.`);
                }
                throw new Error(`API request failed with status ${response.status}: ${errorText.substring(0, 200)}`);
            }

            const data = await response.json();
            console.log("Claude API response received successfully via proxy");
            return data; // Success!

        } catch (error) {
             console.error(`Attempt ${attempts + 1}/${maxAttempts} failed:`, error.name, error.message);
             lastError = error; // Store the last error encountered
             attempts++;
             if (attempts < maxAttempts) {
                 // Exponential backoff delay
                 const delay = Math.pow(2, attempts -1) * this.initialRetryDelay;
                 console.log(`Retrying in ${delay}ms...`);
                 await new Promise(resolve => setTimeout(resolve, delay));
             } else {
                 console.error("All Claude API attempts failed after multiple retries.");
                 throw lastError; // Re-throw the last encountered error
             }
        }
    } // End while loop

    // Fallback if the loop somehow finishes without throwing (should not happen)
    console.warn("callClaudeApiWithRetry: Exited loop unexpectedly. Returning fallback.");
    return this.createFallbackResponse(prompt);
  } // End callClaudeApiWithRetry method

  /**
   * Creates a fallback response object if the API call fails completely.
   */
  createFallbackResponse(prompt) {
    console.log("Creating fallback response for prompt starting with:", prompt.substring(0, 50));
    const isGameOverTitle = prompt.includes('game over title');
    const isAchievement = prompt.includes('personalized achievement');
    const isClassify = prompt.includes('classification game');

    if (isGameOverTitle) { return { content: [{ text: "GALACTIC ENDEAVOR" }] }; }
    if (isAchievement) { return { content: [{ text: "NAME: PERSISTENT PLAYER\nDESCRIPTION: You navigated the cosmos with determination!" }] }; }
    if (isClassify) {
      console.warn("Returning hardcoded fallback classification words due to API failure.");
      const fallbackWords = JSON.stringify([
          { "word": "Motherboard", "category": "Hardware" }, { "word": "Firewall", "category": "Software" },
          { "word": "Keyboard", "category": "Hardware" }, { "word": "Database", "category": "Software" },
          { "word": "CPU", "category": "Hardware" }, { "word": "Browser", "category": "Software" },
          { "word": "RAM", "category": "Hardware" }, { "word": "Spreadsheet", "category": "Software" },
          { "word": "Monitor", "category": "Hardware" }, { "word": "Compiler", "category": "Software" }
      ]);
      return { content: [{ text: fallbackWords }] };
    }
    // Generic fallback for unknown prompt types
    return { content: [{ text: "Processing..." }] };
  }

  /**
   * Robustly extracts the primary text content from various possible Claude API response structures.
   */
  extractResponse(response) {
    try {
      if (!response) { console.warn("extractResponse: Received null or undefined response."); return ""; }
      // Standard expected format: { content: [ { text: "..." } ] }
      if (response.content && Array.isArray(response.content) && response.content.length > 0 && typeof response.content[0].text === 'string') { return response.content[0].text.trim(); }
      // Handle cases where 'text' might be directly on the response
      if (response.text && typeof response.text === 'string') { return response.text.trim(); }
      // Handle if the response itself is just a string
      if (typeof response === 'string') { return response.trim(); }
      // Handle older/different message structures if encountered
      if (response.message && response.message.content && typeof response.message.content === 'string') { return response.message.content.trim(); }
      // If it's an error object from the API
      if (response.error && response.error.message && typeof response.error.message === 'string') { console.warn("extractResponse: API returned an error structure:", response.error.message); return ""; }

      // Deep search for a 'text' or 'content' string property as a last resort
      const searchForText = (obj, depth = 0) => {
        if (depth > 4) return null;
        if (typeof obj !== 'object' || obj === null) return null;
        if (obj.text && typeof obj.text === 'string') return obj.text;
        if (obj.content && typeof obj.content === 'string') return obj.content;
        for (const key in obj) { if (obj.hasOwnProperty(key)) { const result = searchForText(obj[key], depth + 1); if (result) return result; } }
        return null;
      };
      const textFound = searchForText(response);
      if (textFound) { console.warn("extractResponse: Found text via deep search, structure might be unexpected."); return textFound.trim(); }

      // Log inability to extract and return empty
      console.warn("extractResponse: Couldn't extract text content from response:", JSON.stringify(response).substring(0, 150));
      return "";
    } catch (error) { console.error("extractResponse: Error during extraction:", error); return ""; }
  }

} // End claudeAPIService class